import * as mobx from 'mobx';
import { z } from 'zod';
import { ConfigOf, Connection, Domain, MutableOf, StateOf } from './base';
import { DeltaBase } from './deltas';
import { RecordDomain, RecordFields } from './record';

/* Mapping of object.type -> fields for that type */
type RecordUnionFields = Record<string, RecordFields>;

type MutableBase<U extends RecordUnionFields, D> = D extends keyof U
  ? {
      readonly type: D;
    } & ConfigOf<RecordDomain<U[D]>>['mutableBase']
  : never;
type State<U extends RecordUnionFields, D> = D extends keyof U
  ? {
      readonly type: D;
    } & ConfigOf<RecordDomain<U[D]>>['state']
  : never;

type RecordUnionConfig<U extends RecordUnionFields> = {
  mutableBase: MutableBase<U, keyof U>;
  state: State<U, keyof U>;
  delta: DeltaBase; // no deltas
};

/**
 * Record union
 *
 * A record of multiple union domains, e.g:
 *
 *   new RecordUnionDomain({
 *     inline: {
 *       value: fooDomain,
 *     },
 *     reference: {
 *       id: new AtomDomain(z.string()),
 *     },
 *   })
 *
 * States for this domain would include:
 *
 *   { type: 'inline', value: foo }
 *   { type: 'reference', id: 'abc' }
 */
export class RecordUnionDomain<U extends RecordUnionFields>
  implements Domain<RecordUnionConfig<U>>
{
  private readonly domainSymbol = Symbol('record union');
  private readonly domains = new Map<keyof U, RecordDomain<U[keyof U]>>();
  readonly stateSchema: Domain<RecordUnionConfig<U>>['stateSchema'];

  constructor(allFields: U) {
    for (const [k, fields] of Object.entries(allFields)) {
      this.domains.set(k as keyof U, new RecordDomain(fields as any));
    }
    this.stateSchema = z.discriminatedUnion(
      'type',
      // 어머니의 눈물이 흐르네요  (╥﹏╥)
      // thanks copilot for teaching me this
      [...this.domains.entries()].map(([k, domain]) =>
        z.object({ type: z.literal(k) }).merge(domain.stateSchema as any),
      ) as any,
    ) as Domain<RecordUnionConfig<U>>['stateSchema'];
  }

  create(
    oid: string,
    initialState: StateOf<RecordUnionConfig<U>>,
    cxn: Connection,
  ) {
    const { type } = initialState;
    const domain = this.domains.get(type);
    if (!domain) {
      const valid = [...this.domains.keys()].join(' | ');
      throw new Error(`Unknown type ${type.toString()}; expected ${valid}`);
    }
    const mutable = domain.create(oid, initialState, cxn) as MutableOf<
      RecordUnionConfig<U>
    >;
    const wrappedState = mobx.computed(() => ({ ...mutable.state, type }));
    return new Proxy(mutable, {
      get: (target, prop) => {
        if (prop === 'type') {
          return type;
        }
        if (prop === this.domainSymbol) {
          return true;
        }
        if (prop === 'state') {
          return wrappedState.get();
        }
        return Reflect.get(target, prop);
      },
    });
  }

  getter(m: MutableOf<RecordUnionConfig<U>>) {
    return m;
  }

  statesEqual(
    a: StateOf<RecordUnionConfig<U>>,
    b: StateOf<RecordUnionConfig<U>>,
  ) {
    if (a.type !== b.type) {
      return false;
    }
    const domain = this.domains.get(a.type)!;
    return domain.statesEqual(a, b);
  }

  isMutable(m: unknown): m is MutableOf<RecordUnionConfig<U>> {
    return (
      typeof m === 'object' && m != null && m.hasOwnProperty(this.domainSymbol)
    );
  }
}
